import type { JSX } from 'react';

import type { FachgruppeDetails, FachgruppeList } from '@asas/domain';

import type { FehlerBefund } from '../daten/fehler';
import { datumAnzeige } from '../mitarbeiter/formular';

import { bisAnzeige, MITGLIED_STATUS_LABELS, seitAnzeige } from './fachgruppen';

/**
 * Die Ansichten der Fachgruppen (AC-104).
 *
 * Reine Ansichten wie in `mitarbeiter/MitarbeiterAnsicht.tsx`: Daten und
 * Rueckrufe hinein, Markup hinaus. Zustand, Netz und Fehlerbehandlung liegen
 * an EINER Stelle (`FachgruppenBereich`). Bewusst schmucklos; die Portierung
 * des Mockups (E2 ff.) ersetzt die Darstellung.
 */

export interface FachgruppenKopfzeileProps {
  readonly geladen: boolean;
  readonly laedt: boolean;
  readonly sendet: boolean;
  /** Der Befund des LETZTEN Ladeversuchs — auch dann, wenn schon eine Liste dasteht. */
  readonly ladefehler: FehlerBefund | null;
  /** Mit `fachgruppe:manage`? Ohne das Recht gibt es den Knopf „anlegen" gar nicht. */
  readonly pflegbar: boolean;
  /** Nach einer Ablehnung beim Anlegen oder Umbenennen (403): bis zum Neuladen der Seite kein weiterer Versuch. */
  readonly pflegeGesperrt: boolean;
  readonly anmeldeUrl: string;
  readonly onLaden: () => void;
  readonly onNeu: () => void;
}

/**
 * Oeffnen, Neu laden, Anlegen. Ein nicht wiederholbarer Befund (403) SPERRT
 * das Laden: jeder weitere Klick schriebe eine unloeschbare Audit-Zeile (§19,
 * §20.2 Punkt 3).
 */
export function FachgruppenKopfzeile({
  geladen,
  laedt,
  sendet,
  ladefehler,
  pflegbar,
  pflegeGesperrt,
  anmeldeUrl,
  onLaden,
  onNeu,
}: FachgruppenKopfzeileProps): JSX.Element {
  const ladenGesperrt = laedt || sendet || (ladefehler !== null && !ladefehler.wiederholbar);

  return (
    <>
      {ladefehler === null ? null : <p role="alert">{ladefehler.meldung}</p>}
      {ladefehler?.neuAnmelden === true ? (
        <p>
          <a href={anmeldeUrl}>Neu anmelden</a>
        </p>
      ) : null}
      <p>
        {geladen && pflegbar ? (
          <>
            <button type="button" disabled={sendet || pflegeGesperrt} onClick={onNeu}>
              Fachgruppe anlegen
            </button>{' '}
          </>
        ) : null}
        <button type="button" disabled={ladenGesperrt} onClick={onLaden}>
          {laedt ? 'Wird geladen …' : geladen ? 'Neu laden' : 'Fachgruppen öffnen'}
        </button>
      </p>
    </>
  );
}

export interface FachgruppenListeProps {
  readonly liste: FachgruppeList;
  /** Die geoeffnete Fachgruppe, hervorgehoben. */
  readonly offen: string | null;
  /** Waehrend ein Schreibzugriff laeuft, wechselt die Auswahl nicht. */
  readonly sendet: boolean;
  /** Wie in der Kopfzeile: ohne `fachgruppe:manage` kein Knopf „umbenennen". */
  readonly pflegbar: boolean;
  /** Wie in der Kopfzeile: nach einer Ablehnung kein weiterer Versuch. */
  readonly pflegeGesperrt: boolean;
  readonly onOeffnen: (id: string) => void;
  readonly onUmbenennen: (id: string) => void;
}

export function FachgruppenListe({
  liste,
  offen,
  sendet,
  pflegbar,
  pflegeGesperrt,
  onOeffnen,
  onUmbenennen,
}: FachgruppenListeProps): JSX.Element {
  if (liste.fachgruppen.length === 0) {
    return <p>Noch keine Fachgruppen angelegt.</p>;
  }

  return (
    <table>
      <caption>Beschäftigte am {datumAnzeige(liste.on)}</caption>
      <thead>
        <tr>
          <th scope="col">Fachgruppe</th>
          <th scope="col">Beschäftigte</th>
          <th scope="col">Aktion</th>
        </tr>
      </thead>
      <tbody>
        {liste.fachgruppen.map((fachgruppe) => (
          <tr key={fachgruppe.id} aria-current={fachgruppe.id === offen ? 'true' : undefined}>
            <td>{fachgruppe.name}</td>
            <td>{fachgruppe.memberCount}</td>
            <td>
              <button
                type="button"
                disabled={sendet}
                aria-label={`${fachgruppe.name} öffnen`}
                onClick={() => {
                  onOeffnen(fachgruppe.id);
                }}
              >
                Öffnen
              </button>
              {pflegbar ? (
                <>
                  {' '}
                  <button
                    type="button"
                    disabled={sendet || pflegeGesperrt}
                    aria-label={`${fachgruppe.name} umbenennen`}
                    onClick={() => {
                      onUmbenennen(fachgruppe.id);
                    }}
                  >
                    Umbenennen
                  </button>
                </>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export interface FachgruppeMitgliederProps {
  readonly details: FachgruppeDetails;
  /** Der eingegebene Stichtag, `''` = heute laut Server. */
  readonly stichtag: string;
  /** Heute laut Server (`fachgruppen.list`): ein spaeterer Stichtag ist unzulaessig. */
  readonly heute: string;
  readonly laedt: boolean;
  /** Nach einer Ablehnung (403) wird nicht erneut geladen — jeder Versuch schriebe eine Audit-Zeile. */
  readonly gesperrt: boolean;
  readonly onStichtag: (wert: string) => void;
  readonly onAnzeigen: () => void;
}

/**
 * Wer der geoeffneten Fachgruppe am Stichtag angehoert (AK-1). Ein Stichtag in
 * der Vergangenheit zeigt die damalige Besetzung (AK-3). Einen in der Zukunft
 * weist der Server ab (er verriete Austrittsdaten); `max` sagt es vorher.
 */
export function FachgruppeMitglieder({
  details,
  stichtag,
  heute,
  laedt,
  gesperrt,
  onStichtag,
  onAnzeigen,
}: FachgruppeMitgliederProps): JSX.Element {
  return (
    <section aria-label={`Fachgruppe ${details.name}`}>
      <h3>{details.name}</h3>
      <p>
        <label>
          Stichtag{' '}
          <input
            type="date"
            value={stichtag}
            max={heute}
            onChange={(event) => {
              onStichtag(event.target.value);
            }}
          />
        </label>{' '}
        <button type="button" disabled={laedt || gesperrt} onClick={onAnzeigen}>
          {laedt ? 'Wird geladen …' : 'Anzeigen'}
        </button>
      </p>
      {details.members.length === 0 ? (
        <p>Am {datumAnzeige(details.on)} ist dieser Fachgruppe niemand zugeordnet.</p>
      ) : (
        <table>
          <caption>Zugeordnet am {datumAnzeige(details.on)}</caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Status</th>
              <th scope="col">Zugeordnet</th>
              <th scope="col">Geplanter Wechsel</th>
            </tr>
          </thead>
          <tbody>
            {details.members.map((member) => (
              <tr key={member.employeeId}>
                <td>{member.name}</td>
                <td>{MITGLIED_STATUS_LABELS[member.status]}</td>
                <td>{seitAnzeige(member)}</td>
                <td>{bisAnzeige(member)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export interface NamensFormularProps {
  readonly modus: 'neu' | 'umbenennen';
  readonly name: string;
  readonly fehler: string | null;
  readonly sendet: boolean;
  /** Nach einer Ablehnung (403) bleibt Speichern gesperrt — jeder Versuch schriebe eine Audit-Zeile. */
  readonly gesperrt: boolean;
  readonly onAendern: (wert: string) => void;
  readonly onSpeichern: () => void;
  readonly onAbbrechen: () => void;
}

export function NamensFormular({
  modus,
  name,
  fehler,
  sendet,
  gesperrt,
  onAendern,
  onSpeichern,
  onAbbrechen,
}: NamensFormularProps): JSX.Element {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSpeichern();
      }}
    >
      <h3>{modus === 'neu' ? 'Fachgruppe anlegen' : 'Fachgruppe umbenennen'}</h3>
      <p>
        <label htmlFor="fachgruppe-name">Name der Fachgruppe</label>{' '}
        {/* 100 = FACHGRUPPE_NAME_MAX_LENGTH; der Hauptpfad von @asas/domain ist im
            Browser nur als Typ erlaubt (Regel 22). Entscheiden tut der Server. */}
        <input
          id="fachgruppe-name"
          type="text"
          required
          maxLength={100}
          value={name}
          disabled={sendet}
          aria-invalid={fehler !== null}
          {...(fehler === null ? {} : { 'aria-describedby': 'fachgruppe-name-fehler' })}
          onChange={(event) => {
            onAendern(event.target.value);
          }}
        />
        {fehler === null ? null : <span id="fachgruppe-name-fehler"> {fehler}</span>}
      </p>
      <p>
        <button type="submit" disabled={sendet || gesperrt}>
          {sendet ? 'Wird gespeichert …' : 'Speichern'}
        </button>{' '}
        <button type="button" disabled={sendet} onClick={onAbbrechen}>
          Abbrechen
        </button>
      </p>
    </form>
  );
}
